'use client';

import React, { forwardRef } from 'react';
import { useTheme } from '../context/ThemeProvider';
import styles from './GlobalDropdown.module.css';

export interface GlobalDropdownOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface GlobalDropdownProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  options: GlobalDropdownOption[];
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  error?: string;
}

export const GlobalDropdown = forwardRef<HTMLSelectElement, GlobalDropdownProps>(
  ({ 
    label, 
    value, 
    onChange, 
    options, 
    placeholder = "Select an option...",
    disabled = false,
    required = false,
    className = '',
    error
  }, ref) => {
    const { theme } = useTheme();
    const isDarkMode = theme === 'dark';

    return (
      <div className={`${styles.container} ${className}`}>
        {label && (
          <label className={styles.label}>
            {label}
            {required && <span className={styles.required}>*</span>}
          </label>
        )}
        <div className={styles.selectWrapper}>
          <select
            ref={ref}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            required={required}
            className={`${styles.select} ${error ? styles.error : ''} ${disabled ? styles.disabled : ''} ${value ? styles.hasValue : ''}`}
          >
            <option value="" disabled>
              {placeholder}
            </option>
            {options.map((option) => (
              <option 
                key={option.value} 
                value={option.value}
                disabled={option.disabled}
              >
                {option.label}
              </option>
            ))}
          </select>
          
          <img 
            src={isDarkMode ? "/assets/icons/chevron.down.svg" : "/assets/icons/chevron.down.black.svg"} 
            alt="Dropdown" 
            width={8} 
            height={8} 
            className={styles.chevron}
          />
        </div>
        
        {error && (
          <span className={styles.errorMessage}>
            {error}
          </span>
        )}
      </div>
    );
  }
);

GlobalDropdown.displayName = 'GlobalDropdown';
